import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from "@ngx-formly/core";
import { MessageService } from "primeng/api";
import {
  catchError,
  EMPTY,
  interval,
  last,
  map,
  Observable,
  retry,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  timer
} from "rxjs";
import { TestCaseGeneratorActions } from "../../../core/ngrx/actions/testcase-generator.actions";
import {
  selectFileConnections,
  selectJiraConnections,
  selectLlmConnections,
} from "../../../core/ngrx/selectors/connection.selectors";
import {
  selectAllJiraStories,
  selectError,
  selectGenerating,
  selectLoading
} from "../../../core/ngrx/selectors/testcase-generator.selectors";
import { CommonService } from "../../../core/services/common.service";
import { ProjectService } from "../../../core/services/project/project.service";
import { RestService } from "../../../core/services/rest.service";
import { TcgService } from "../../../core/services/tcg.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { MaterialModule } from "../../../core/shared/material.module";
import { userStoriesMoreDetails, userStoriesTableColumns } from "../../../core/table-templates/test-case-table";
import {
  userStoryTableColumns
} from "../../../core/table-templates/user-story-table";
import { MessageEnum } from "../../../core/utils/message.enum";
import { DynamicFormComponent } from "../../common/dynamic-form/dynamic-form.component";
import { FileUploadComponent } from "../../common/file-upload/file-upload.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { DeleteConfirmationComponent } from "../../modal-templates/delete-confirmation/delete-confirmation.component";
import { RciQualityReportModalComponent } from "../../modal-templates/rci-quality-report-modal/rci-quality-report-modal.component";
import { SharedComponents } from "../../shared.module";
import { TestCaseTableComponent } from "../test-case-table/test-case-table.component";

/**
 * UserStoriesComponent - A component to manage and display user stories.
 */
@Component({
  selector: "app-user-stories",
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    FormlyModule,
    FormlyBootstrapModule,
    FileUploadComponent,
    DynamicFormComponent,
    TestCaseTableComponent,
    LoadingComponent,
    ...SharedComponents,
  ],
  templateUrl: "./user-stories.component.html",
  styleUrl: "./user-stories.component.scss",
})
export class UserStoriesComponent {
  /**
   * Input data for the component.
   * @type {any}
   */
  @Input() data!: any;

  /**
   * Emits an event to move to the next tab.
   * @type {EventEmitter<void>}
   */
  @Output() moveToNextTabEvent = new EventEmitter<void>();
  private messageService = inject(MessageService);
  /**
   * Emits an event to enable specific tabs.
   * @type {EventEmitter<number[]>}
   */
  @Output() enableTabsEvent = new EventEmitter<number[]>();

  /** Observables for managing connections and data streams. */
  jiraConnections$!: Observable<any[]>;
  llmConnections$!: Observable<any[]>;
  fileConnections$!: Observable<any[]>;
  jiraStories$!: Observable<any[]>;
  testCases$!: Observable<any[]>;
  testCasesByVersionId$!: Observable<any[]>;
  testCaseVersions$!: Observable<any[]>;
  loading$!: Observable<boolean>;
  loadingTestCases$!: Observable<boolean>;
  error$!: Observable<string | null>;
  generating$!: Observable<boolean>;
  tableDataSet$!: Observable<any[]>;

  /** Form and configuration properties. */
  form = new FormGroup({});
  model: any = {
    llmConnection: "",
    connectionType: "jira",
    connection: "",
  };
  versionHistoryModel: any = {};
  options: FormlyFormOptions = {};

  fields: FormlyFieldConfig[] = [];
  versionHistoryField: FormlyFieldConfig[] = [];
  isGenerate: boolean = false;

  /** Table data configuration. */
  tableData: {
    headers?: string;
    columns: any[];
    data: any[];
  } = {
      headers: "",
      columns: [],
      data: [],
    };
  projectId: string;
  showNoData: boolean = false;
  private modalService = inject(NgbModal);

  selectedRows: any = []; // to get seletcted rows
  storageConnections: any = [];
  llmCons: any = [];
  jiraCons: any = [];

  selectedIndex: any = 0;
  isExpanded: boolean = true;
  selectedFile: any = null;
  fileUploadConfig = {
    fileUploadLabel: " ",
    fileTypes: ".pdf, .docx",
    buttonlabelPrefix: "Drag & drop your file here or",
    buttonLabel: "Choose file",
    placeholderText: "Only.PDF & .DOCX",
  };
  steps: any = [
    {
      title: "Extract User Stories ",
    },
    {
      title: "Upload Application Context (optional)",
    },
  ];
  userStoryFileConfig = {
    fileUploadLabel: " ",
    fileTypes: ".pdf, .docx, .xlsx",
    buttonLabel: "Choose file",
    placeholderText: "Only .PDF .XLSX & .DOCX",
  };
  selectedStoryFile: any = null;

  userStoryExtractOptions = [
    {
      value: "alm",
      title: "ALM connection",
      description: "Jira/Rally/Azure DevOps",
    },
    {
      value: "doc",
      title: "Requirement Document",
      description: "PDF/XLSX/DOCX",
    },
    { value: "storage", title: "Other Sources", description: "S3/XLSX" },
  ];
  selectedStoryOption = "alm";
  dynamicForm: any = new FormGroup({});
  storageForm: any = new FormGroup({});
  isExtractDisabled: boolean = false;
  storageFields: any = [
    {
      name: "Connection Type",
      type: "select",
      id: "type",
      key: "type",
      placeholder: "Connection Type",
      options: [],
      wrapperClass: "col-md-6",
    },
    {
      name: "Connection Name",
      type: "select",
      id: "name",
      key: "name",
      placeholder: "Connection Name",
      options: [],
      wrapperClass: "col-md-6",
    },
  ];
  almFields: any = [
    {
      name: "Connection Provider",
      type: "select",
      id: "provider",
      key: "provider",
      placeholder: "Connection Provider",
      options: [],
      wrapperClass: "col-md-6",
    },
    {
      name: "Connection Name",
      type: "select",
      id: "name",
      key: "name",
      placeholder: "Connection Name",
      wrapperClass: "col-md-6",
      options: [],
    },
    {
      key: "fromTimestamp",
      name: "Extract From",
      type: "date-picker",
      disableFutureDates: true,
      // validations: {
      //   required: true,
      // },
      wrapperClass: "col-md-6",
    }
  ];
  tableConfigs: any = {
    header: "User Story Summary",
    columns: userStoriesTableColumns,
    rows: [],
    titleKey: "title", //to highlight the title content
    isEdit: false, //edit button on top right, editable fields
    isFeedback: false, //to add feedback
    moreDetails: userStoriesMoreDetails
  };
  panelHeader: string = "Extract User Stories & Upload Application Context";
  selectedRowsCount: any = 0;
  isUserStoryExtracting: boolean = false;
  progressStatus: any = {
    percentage: 3,
    text: '',
    isError: false
  };
  formData: any;
  isAppContextProgress: boolean = false;
  isProgressError: boolean = false;
  appContextProgressText: any;
  progressPrecentage: number = 0;
  sampleFiles = [
    {
      icon: "../../../../assets/images/pdf.svg",
      name: "User Story Template.pdf",
      type: "PDF",
      loading: false,
    },
    {
      icon: "../../../../assets/images/docx.svg",
      name: "User Story Template.docx",
      type: "DOCX",
      loading: false,
    },
    {
      icon: "../../../../assets/images/excel.svg",
      name: "User Story Template.xlsx",
      type: "XLSX",
      loading: false,
    },
  ];
  uploadedApplicationContext: any = undefined;
  isLoading: boolean = false;
  $: any;
  configId: string = '';
  /** ALM Extract From date — forwarded to progress polls when set. */
  extractFromTimestamp: string | undefined;
  selectedStatus: any = '';
  statusList: any = [];
  isFiltering = false;
  zorroOptions: any = {
    isLazyLoad: true
  };
  tableMeta = {
    totalItems: 0,
    pageSize: 10,
    currentPage: 1,
    isSelectAll:false,
    deselectedRows:[]
  };

  stopPolling$ = new Subject<void>();
  destroy$ = new Subject<void>();
  isSelectAll:boolean = false;
  deselectedRows:any = [];

  /**
   * Constructs the UserStoriesComponent.
   * @param {Store} store - NgRx store for state management.
   * @param {ActivatedRoute} route - ActivatedRoute for route information.
   * @param {ChangeDetectorRef} cdr - Change detector for manual updates.
   * @param {UtilitiesService} utilitiService - Service for utility functions.
   */
  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private utilitiService: UtilitiesService,
    private commonService: CommonService,
    private restService: RestService,
    private readonly toaster: ToasterService,
    private readonly projectService: ProjectService,
    private readonly tcgService: TcgService
  ) {
    this.projectId = this.route.snapshot.paramMap.get("id") as string;

    // Load Store Data and set values
    this.loadStoreData();

    // Initilaize form data
    // this.setupFields();
  }

  /**
   * Lifecycle hook that initializes the component.
   */
  ngOnInit() {
    this.selectedRows = [];
    this.getApplicationContext();
    this.tableData = this.getData();
    this.loadConnections();

    this.dynamicForm.valueChanges.subscribe(() => this.updateExtractDisabled());
    this.storageForm.valueChanges.subscribe(() => this.updateExtractDisabled());
    this.utilitiService.dispatchIfEmpty(
      this.jiraStories$,
      TestCaseGeneratorActions.loadJiraStories({
        projectId: this.projectId,
        jiraConnection: this.model?.jiraConnection?.id,
      })
    );
    // Trigger change detection manually
    this.cdr.detectChanges();
  }

  getApplicationContext() {
    this.isLoading = true;
    this.restService.handleAsyncApplicationContext(this.projectId).subscribe(
      (item: any) => {
        console.log("ITEM", item);
        this.uploadedApplicationContext = item?.data;
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        this.toaster.pop({
          type: "error",
          text: MessageEnum.APP_CONTEXT_LOAD_FAILURE,
        });
      }
    );
  }

  decodeFileName(fileName: string): string {
    return decodeURIComponent(fileName);
  }

  getImagePath(fileName: string) {
    const decodedName = decodeURIComponent(fileName); // Converts %20 to space
    const parts = decodedName.split(".");
    const type = parts.length > 1 ? parts.pop()?.toLowerCase() || "" : "";
    return `../../../../assets/images/${type}.svg`;
  }

  deleteApplicationContext(data: any) {
    this.restService.deleteApplicationContext(this.projectId).subscribe(
      (item) => {
        this.toaster.pop({
          type: "success",
          text: MessageEnum.APP_CONTEXT_DELETE_SUCCESS,
        });
        this.uploadedApplicationContext = undefined;
      },
      (error) => {
        this.toaster.pop({
          type: "error",
          text: MessageEnum.APP_CONTEXT_DELETE_FAILURE,
        });
      }
    );
  }

  loadConnections() {
    this.restService.getConnections(this.projectId).subscribe((res: any) => {
      if (res?.data) {
        this.storageConnections = res?.data?.FILE;
        this.llmCons = res?.data?.LLM;
        this.jiraCons = res?.data?.ALM;
        this.onRadioSelect("alm");
      }
    });
  }

  /**
   * Sets default values for form fields based on connections.
   */
  setDefaultValues() {
    this.jiraConnections$.pipe(take(1)).subscribe((jiraConnections) => {
      if (jiraConnections.length) {
        this.model.jiraConnection = jiraConnections[0].value;
      }
    });

    this.llmConnections$.pipe(take(1)).subscribe((llmConnections) => {
      if (llmConnections.length) {
        this.model.llmConnection = llmConnections[0].value;
      }
    });

    this.fileConnections$.pipe(take(1)).subscribe((fileConnections) => {
      if (fileConnections.length) {
        this.model.fileConnection = fileConnections[0].value;
      }
    });
  }

  /**
   * Loads data from the store and initializes observables.
   */
  loadStoreData() {
    this.store.select(selectAllJiraStories).subscribe((res: any) => {
      this.tableData.data = res;
    });
    this.loading$ = this.store.select(selectLoading);
    this.generating$ = this.store.select(selectGenerating);
    this.error$ = this.store.select(selectError);

    this.llmConnections$ = this.store.select(selectLlmConnections).pipe(
      map((connections) =>
        connections.map((connection: any) => ({
          value: connection,
          label: connection.connectionName,
        }))
      )
    );

    this.jiraConnections$ = this.store.select(selectJiraConnections).pipe(
      map((connections) =>
        connections.map((connection: any) => ({
          value: connection,
          label: connection.connectionName,
        }))
      )
    );

    this.fileConnections$ = this.store.select(selectFileConnections).pipe(
      map((connections) =>
        connections.map((connection: any) => ({
          value: connection,
          label: connection.connectionName,
        }))
      )
    );

    // this.setDefaultValues();
  }

  /**
   * Submits the form data for processing.
   */
  onSubmit() {
    if (this.model.connectionType === "jira") {
      // this.tableData.data = this.jiraStories$;
      const llmConnection = this.model?.llmConnection;
      this.commonService.setLLMConnection(llmConnection);
      this.store.dispatch(
        TestCaseGeneratorActions.loadJiraStories({
          projectId: this.projectId,
          jiraConnection: this.model?.connection,
        })
      );
    } else {
      this.restService
        .getStorageUserStories(this.projectId, this.model.connection)
        .subscribe((res: any) => {
          this.tableData.data = res?.data?.stories;
        });
    }
  }

  onDownload() { }

  /**
   * Moves to the next tab.
   */
  moveToNextTab() {
    this.moveToNextTabEvent.emit();
  }

  /**
   * Sets up fields for the Formly form.
   */
  setupFields() {
    this.fields = [
      {
        key: "llmConnection",
        type: "select",
        templateOptions: {
          label: "Select LLM Connection",
          placeholder: "Select",
          options: this.llmConnections$,
          required: true,
        },
      },
      {
        key: "jiraConnection",
        type: "select",
        templateOptions: {
          label: "Select Jira Connection",
          placeholder: "Select",
          options: this.jiraConnections$,
        },
      },
      {
        key: "fileConnection",
        type: "select",
        templateOptions: {
          placeholder: "User Story Document",
          options: this.fileConnections$,
        },
        expressionProperties: {
          "templateOptions.disabled": (model: any) => {
            return !!model.jiraConnection;
          },
        },
      },
    ];
  }

  handleRowSelection(event: any) {
    // this.selectedRows = event.data.filter((obj: any) => obj.select);
    const newlySelected = event.data.filter((obj: any) => obj.select);

    // Merge existing selected rows and new
    this.selectedRows = [
      ...this.selectedRows.filter(
        (prev: any) => !newlySelected.some((curr: any) => curr.id === prev.id)
      ),
      ...newlySelected
    ];

    this.selectedRows = this.selectedRows.filter((item: any) => item.select);

    if (event.deselectedData) {
      const newData = event.deselectedData;
    
      this.deselectedRows = [
        ...this.deselectedRows,
        ...newData
      ].filter(
        (row, index, self) =>
          index === self.findIndex(r => r.storyId === row.storyId)
      );
    }
    
    this.selectedRowsCount =
      this.selectedRows.length === 0
        ? 0
        : this.selectedRows.length.toString().padStart(2, "0");
        
    if(event?.select_type && event?.select_type ==='all' ){
      this.isSelectAll = true;
      this.selectedRows = [];
      this.selectedRowsCount = this.tableMeta.totalItems - this.deselectedRows.length;
      console.log("deselected rows: ", this.deselectedRows);
    } else {
      this.isSelectAll = false;
      this.deselectedRows = [];
    }
    this.tableMeta.isSelectAll = this.isSelectAll;
    this.tableMeta.deselectedRows = this.deselectedRows;
  }

  /**
   * Retrieves table data for user stories.
   * @returns {Object} Table data configuration.
   */
  getData() {
    this.selectedRows = [];
    return {
      headers: "User Stories",
      columns: userStoryTableColumns,
      data: [],
    };
  }

  onConnectionTypeChange() {
    // if(this.model.connectionType === 'jira') {
    //   this.model.connection = this.jiraCons.length && this.jiraCons[0]?.id;
    // } else {
    //   this.model.connection = this.storageConnections.length && this.storageConnections[0]?.id;
    // }
  }

  onStepClick(index: number) {
    this.selectedIndex = index;
  }

  uploadAppContext() {
    let formData = new FormData();
    formData.append("file", this.selectedFile);
    this.isAppContextProgress = true;
    this.appContextProgressText = "Appcontext Processing Started";
    this.progressPrecentage = 3;
    this.restService
      .uploadAppContext(this.projectId, formData)
      .pipe(
        switchMap((response: any) =>
          this.restService
            .processApplicationContext(this.projectId, response?.data?.id)
            .pipe(
              switchMap(() =>
                interval(20000).pipe(
                  switchMap(() =>
                    this.restService
                      .handleAsyncApplicationContext(this.projectId)
                      .pipe(
                        map((statusResponse: any) => {
                          if (statusResponse?.data?.status === "COMPLETED") {
                            this.progressPrecentage = 100;
                            this.appContextProgressText =
                              statusResponse?.data?.message;
                            this.uploadedApplicationContext =
                              statusResponse?.data;
                            this.selectedFile = null;
                            setTimeout(() => {
                              this.isAppContextProgress = false;
                            }, 2000);
                            return { success: true };
                          } else if (
                            statusResponse?.data?.status === "FAILED"
                          ) {
                            this.isAppContextProgress = false;
                            this.appContextProgressText =
                              statusResponse?.data?.message;
                            this.isProgressError = true;
                            throw new Error(
                              "Application context generation failed."
                            );
                          } else {
                            this.appContextProgressText =
                              statusResponse?.data?.message;
                            this.progressPrecentage =
                              statusResponse?.data?.totalChunks > 0
                                ? Math.round(
                                  (statusResponse?.data?.processingChunks /
                                    statusResponse?.data?.totalChunks) *
                                  100
                                )
                                : this.progressPrecentage;
                            return { success: false };
                          }
                        })
                      )
                  ),
                  takeWhile((result) => !result.success, true),
                  last()
                )
              )
            )
        )
      )
      .subscribe({
        next: () => {
          this.toaster.pop({
            type: "success",
            text: MessageEnum.APP_CONTEXT_UPLOAD_SUCCESS,
          });
        },
        error: (error: any) => {
          this.isAppContextProgress = false;
          this.toaster.pop({
            type: "error",
            text: error?.message || MessageEnum.APP_CONTEXT_UPLOAD_FAILURE,
          });
        },
      });
  }

  handleFilesSelected(event: any) {
    this.selectedFile = event?.files[0];
  }

  onPanelExpand() { }

  //get the provider, connection type list on radio button select change
  onRadioSelect(type: string) {
    switch (type) {
      case "alm":
        this.almFields[0].options = this.jiraCons
          .filter(
            (item: any, index: number, self: any) =>
              index ===
              self.findIndex((t: any) => t.meta?.source === item.meta?.source)
          )
          .map((item: any) => ({
            value: item.meta?.source,
            label: item.meta?.source,
          }));
        if (this.almFields[0].options.length) {
          this.almFields[0].value = this.almFields[0].options[0].value;
          this.dynamicForm?.controls["provider"]?.setValue(
            this.almFields[0].options[0].value
          );
          this.onSelectProvider({
            field: { id: "provider" },
            value: this.almFields[0].options[0].value,
          });
        }
        break;

      case "storage":
        this.storageFields[0].options = this.storageConnections
          .filter(
            (item: any, index: number, self: any) =>
              index ===
              self.findIndex((t: any) => t.meta?.type === item.meta?.type)
          )
          .map((item: any) => ({
            value: item.meta?.type,
            label: item.meta?.type,
          }));
        if (this.storageFields[0].options.length) {
          this.storageFields[0].value = this.storageFields[0].options[0].value;
          this.onSelectProvider({
            field: { id: "type" },
            value: this.storageFields[0].options[0].value,
          });
        }
        break;

      case "doc":
        break;

      default:
        break;
    }
    this.updateExtractDisabled();
  }

  //get values on dropdown change
  onSelectProvider(event: any) {
    if (event.field.id == "provider") {
      this.almFields[1].options = this.getConnectionsBySource(event?.value);
    } else if (event.field.id == "type") {
      this.storageFields[1].options = this.getConnectionsBySource(event?.value);
    }
  }

  //get connections list by provider/ type
  getConnectionsBySource(source: string) {
    if (this.selectedStoryOption === "alm") {
      let options = this.jiraCons
        .filter((conn: any) => conn.meta?.source === source)
        .map((conn: any) => ({
          label: conn.connectionName,
          value: conn.id,
        }));
      this.almFields[1].value = options?.length ? options[0].value : null;
      this.dynamicForm?.controls["name"]?.setValue(options[0].value);
      return options;
    } else {
      let options = this.storageConnections
        .filter((conn: any) => conn.meta?.type === source)
        .map((conn: any) => ({
          label: conn.connectionName,
          value: conn.id,
        }));
      this.storageFields[1].value = options?.length ? options[0].value : null;
      return options;
    }
  }

  extractAlmUserStories(payload: any) {
    this.extractFromTimestamp = payload?.fromTimestamp;
    this.restService.extractUserStoriesALM(payload).pipe(
      retry({
        count: 1,
        delay: (error, retryCount) => {
          console.log(`Retry attempt ${retryCount} after error:`, error);
          // Exponential backoff: 1s, 2s delays
          return timer(retryCount * 1000);
        }
      }),
      catchError((error) => {
        console.error("Error extracting ALM user stories after retries:", error);
        this.isExpanded = true;
        this.isUserStoryExtracting = false;
        this.toaster.pop({
          type: "error",
          text: error?.error?.message || error?.message || MessageEnum.USER_STORIES_EXTRACTION_FAILURE,
        });
        return EMPTY;
      })
    ).subscribe({
      next: (response) => {
        this.configId = response.data.configId;
        this.tcgService.setConfigId(this.configId);
        this.getAlmStatusList();
        this.asyncProgress(undefined, this.extractFromTimestamp);
      }
    });
  }

  openRciReport(data: any) {
    let modalRef = this.modalService.open(RciQualityReportModalComponent, {
      centered: true,
      size: "xl",
      backdrop: 'static',
    });

    modalRef.componentInstance.configs = {
      projectId: this.projectId,
      configId: this.configId,
      reportId: data
    };
    // modalRef.result.then((result: any) => {

    // });
  }

  handleActions(event: any) {
    if (event.type == 'open-report') {
      this.openRciReport(event.data);
    }
  }

  getAlmStatusList() {
    this.restService.getAlmStatusList(this.projectId, this.configId).subscribe((response: any) => {
      this.statusList = response.data;
      console.log(response);
    }, err => {
      console.error(err?.message || 'Error while loading status list')
    });
  }

  getAlmExtractedStories() {
    let filter = {
      page: this.tableMeta.currentPage - 1,
      size: this.tableMeta.pageSize,
      status: this.selectedStatus
    };
    this.restService.getALMExtractedStories(this.projectId, this.configId, filter).subscribe((response: any) => {
      this.tableMeta.totalItems = response.data.totalElements;
      this.isFiltering = false;
      this.isUserStoryExtracting = false;
      this.updateExtractDisabled();
      if (response.data?.content?.length) {
        // this.tableConfigs.rows = response.data.content;
        this.tableConfigs.rows = response.data?.content?.map((item: any) => {
          const match = this.selectedRows?.find((l1: any) => l1.id === item.id || l1.storyId === item.storyId);
          return {
            ...item,
            select: !!match // true if found
          };
        });
      }
    }, err => {
      console.error(err?.message || 'Error while loading extracted alm stories');
    });
  }

  updateExtractDisabled() {
    // Disable if extraction is already in progress
    if (this.isUserStoryExtracting) {
      this.isExtractDisabled = true;
      return;
    }

    if (this.selectedStoryOption === 'alm') {
      const vals = this.dynamicForm.value;
      this.isExtractDisabled = !vals.provider || !vals.name;
    } else if (this.selectedStoryOption === 'storage') {
      const vals = this.storageForm.value;
      this.isExtractDisabled = !vals.type || !vals.name;
    } else if (this.selectedStoryOption === 'doc') {
      this.isExtractDisabled = !this.selectedStoryFile;
    }
  }

  extractStories() {
    let payload = {};
    this.isExpanded = false;
    switch (this.selectedStoryOption) {
      case "alm":
        const connectionId = this.dynamicForm.value.name;
        const dt = this.dynamicForm.value.fromTimestamp ? this.dynamicForm.value.fromTimestamp.toISOString() : undefined;

        this.restService.getAlmConfigByConnection(this.projectId, connectionId).pipe(
          catchError(() => {
            this.isUserStoryExtracting = true;
            this.updateExtractDisabled();
            payload = {
              projectId: this.projectId,
              almConnection: connectionId,
              fromTimestamp: dt
            };
            this.extractAlmUserStories(payload);
            return EMPTY;
          })
        ).subscribe((res: any) => {
          if (res?.data?.status === 'IN_PROGRESS') {
            // Extraction already running — resume progress bar
            this.configId = res.data.configId;
            this.tcgService.setConfigId(this.configId);
            this.isUserStoryExtracting = true;
            this.updateExtractDisabled();
            this.getAlmStatusList();
            this.asyncProgress(undefined, dt);
            this.toaster.pop({
              type: 'warning',
              text: MessageEnum.RESUME_EXTRACTION,
            });
          } else {
            // Previous extraction finished — start a new extraction
            this.isUserStoryExtracting = true;
            this.updateExtractDisabled();
            payload = {
              projectId: this.projectId,
              almConnection: connectionId,
              fromTimestamp: dt
            };
            this.extractAlmUserStories(payload);
          }
        });
        break;

      case "storage":
        this.isUserStoryExtracting = true;
        this.updateExtractDisabled();

        this.restService
          .getStorageUserStories(this.projectId, this.storageForm?.value?.name)
          .subscribe((res: any) => {
            this.configId = res.data?.configId;
            this.tcgService.setConfigId(this.configId);
            this.asyncProgress();
          });
        break;

      case "doc":
        this.isUserStoryExtracting = true;
        this.updateExtractDisabled();
        this.formData = new FormData();
        this.formData.append("type", "local");
        this.formData.append("file", this.selectedStoryFile);
        this.restService
          .createFileConnection(this.projectId, this.formData)
          .subscribe({
            next: (response: any) => {
              this.restService
                .getStorageUserStories(this.projectId, response?.data?.id)
                .subscribe({
                  next: (res: any) => {
                    this.configId = res.data?.configId;
                    this.tcgService.setConfigId(this.configId);
                    this.asyncProgress();
                    // this.tableConfigs.rows = res?.data?.stories;
                  },
                  error: (error: any) => {
                    this.isUserStoryExtracting = false;
                    this.toaster.pop({
                      type: "error",
                      text: MessageEnum.LOAD_USER_STORIES_FAILURE,
                    });
                  },
                });
            },
            error: (error: any) => {
              this.isUserStoryExtracting = false;
              this.toaster.pop({
                type: "error",
                text: MessageEnum.FILE_UPLOAD_FAILURE,
              });
            },
          });

        break;

      default:
        break;
    }
  }

  asyncProgress(initialPercentage?: number, fromTimestamp?: string) {
    this.progressStatus.text = 'Extracting';
    this.progressStatus.isError = false;
    if (initialPercentage !== undefined) {
      this.progressStatus.percentage = initialPercentage;
    } else {
      this.progressStatus.percentage = 3;
    }
    interval(5000)
      .pipe(
        switchMap(() =>
          this.restService.getUserStoryProgress(
            this.projectId,
            this.configId,
            0,
            10,
            fromTimestamp,
          )
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error("Error tracking user story progress:", error);
          this.toaster.pop({
            type: "error",
            text: error?.error?.message || error?.message || MessageEnum.USER_STORY_PROGRESS_TRACKING_FAILURE,
          });
          this.progressStatus.text = MessageEnum.LOAD_USER_STORIES_FAILURE;
          this.progressStatus.isError = true;
          setTimeout(() => {
            this.isUserStoryExtracting = false;
          }, 2000);
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        this.progressStatus.percentage = res.data.completionPercentage === 0 ? 3 : Math.round(res.data.completionPercentage);
        if (!res.data.completionPercentage && res.data.progress.status.toLowerCase() === 'completed') {
          this.progressStatus.percentage = 100;
        }
        if (res?.data?.progress?.status.toLowerCase() === 'completed') {
          this.getAlmExtractedStories();
          this.stopPolling$.next();
        }
        if (res?.data?.progress?.status.toLowerCase() === "failed") {
          const failureMessage = res?.data?.progress?.tcgKgMessage || res?.data?.message || "User story extraction failed";
          console.error("User story extraction failed:", failureMessage);
          this.toaster.pop({
            type: "error",
            text: failureMessage,
          });
          this.progressStatus.text = MessageEnum.LOAD_USER_STORIES_FAILURE;
          this.progressStatus.isError = true;
          this.stopPolling$.next();
          setTimeout(() => {
            this.isUserStoryExtracting = false;
            this.updateExtractDisabled();
          }, 4000);
        }
      });
  }

  ngOnDestroy() {
    // Clean up subscriptions when component is destroyed
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }


  handleUserStoryFilesSelected(event: any) {
    this.selectedStoryFile = event?.files[0];
    this.updateExtractDisabled();
  }

  generateKG() {
    // let modalRef = this.modalService.open(ChecklistTreeModalComponent, {
    //   centered: true,
    //   backdrop: "static",
    // });
    // modalRef.result.then((result: any) => {
    //   if (result) {
    //     console.log("selected test types->", this.storyTypes[result.value[0]]);
    //     this.selectedUserStoryType = this.storyTypes[result.value[0]];
    //     this.onGenerate();
    //   }
    // });

        // Filter selected stories and extract the "key" values into an array

    const selectedStoryKeys = this.selectedRows
      .filter((item: any) => item.select)
      .map((item: any) => item.storyId);

    const deSelectedStoryKeys = this.deselectedRows
      .map((item: any) => item.storyId);

    console.log("Selected Story Keys:", selectedStoryKeys);
    console.log("De-selected Story Keys:", this.deselectedRows, deSelectedStoryKeys);
    console.log("isSelectAll:", this.isSelectAll);

    this.tcgService.setSelectedRows(this.selectedRows);
    this.tcgService.setStoryKeys(selectedStoryKeys);
    this.tcgService.setDeSelectedRows(this.deselectedRows);
    this.tcgService.setSelectedStatus(this.isSelectAll);

    this.moveToNextTab();
    this.enableTabsEvent.emit([1]);
  }

  removeUserStory() {
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.result.then((result: any) => {
      if (result) {
        // this.store.dispatch(
        //   TestCaseGeneratorActions.deleteJiraStories({
        //     projectId: this.projectId,
        //   })
        // );
      }
    });
  }

  downloadTemplate(type: string) {
    const file = this.sampleFiles.find((f) => f.type === type);

    if (!file) {
      console.error("No file found for type:", type);
      return;
    }

    file.loading = true;
    this.restService
      .getSampleUserStoryFileDownload(this.projectId, type)
      .subscribe(
        {
          next: (item: any) => {
            file.loading = false;
            window.open(item?.data?.signedUrl, "_blank");
          },
          error: (err) => {
            file.loading = false;
            console.error("Download failed", err);
          },
        }
        //   (item: any) => {
        //   window.open(item?.data?.signedUrl, "_blank");
        // }
      );
  }

  onStatusChange(ev: any) {
    this.isFiltering = true;
    this.getAlmExtractedStories();
  }

  onPageChange(event: any) {
    console.log(event);
    this.tableMeta.currentPage = event.page;
    this.tableMeta.pageSize = event.size;
    this.isFiltering = true;
    this.getAlmExtractedStories();
  }
}
